import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";

import Skills from "./Skills";
import { TSkill } from "@utils/about";
import img from "./image.jpeg";

// Define animations
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled components
const AnimatedTypography = styled(Typography)({
  opacity: 0,
  animation: `${fadeInUp} 0.5s ease-out forwards`,
  animationDelay: "0.2s",
});

const AnimatedTitle = styled(Typography)({
  position: "relative",
  display: "inline-block",
  fontWeight: 600,
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: "-4px",
    left: 0,
    width: "100%",
    height: "2px",
    background: "linear-gradient(to right, #536493, transparent)",
  },
  animation: `${fadeInUp} 0.5s ease-out forwards`,
  opacity: 0,
});

type TAboutMeProps = {
  title: string;
  description: string;
  techinalSkillsTitle: string;
  skills: TSkill[];
};

const AboutMe: React.FC<TAboutMeProps> = ({
  title,
  description,
  techinalSkillsTitle,
  skills,
}) => {
  return (
    <Box
      id="About"
      sx={{
        py: 12,
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            gap: { xs: 6, md: 8 },
          }}
        >
          {/* Profile Image */}
          <Box
            sx={{
              position: "relative",
              opacity: 0,
              animation: `${fadeInUp} 0.5s ease-out forwards`,
              "&::before": {
                content: '""',
                position: "absolute",
                width: "100%",
                height: "100%",
                background: "linear-gradient(45deg, #536493 30%, #93c5fd 90%)",
                borderRadius: "20px",
                transform: "rotate(-3deg)",
                zIndex: 0,
              },
            }}
          >
            <Box
              component="img"
              src={img}
              alt="Profile"
              sx={{
                width: { xs: "280px", md: "320px" },
                height: { xs: "280px", md: "320px" },
                objectFit: "cover",
                borderRadius: "20px",
                position: "relative",
                zIndex: 1,
                animation: `${float} 6s ease-in-out infinite`,
                boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
              }}
            />
          </Box>

          {/* Content */}
          <Box 
            sx={{ 
              flex: 1, 
              ml: { md: 5 }, 
              mr: { md: 5 },
            }}
          >
            <AnimatedTitle
              variant="h4"
              sx={{
                mb: 3,
                color: "text.primary",
                animationDelay: "0.3s",
              }}
            >
              {title}
            </AnimatedTitle>

            <AnimatedTypography
              sx={{
                color: "text.secondary",
                mb: 3,
                lineHeight: 1.8,
                animationDelay: "0.4s",
              }}
            >
              {description}
            </AnimatedTypography>

            <AnimatedTitle
              variant="h5"
              sx={{
                mb: 3,
                mt: 2,
                color: "text.primary",
                animationDelay: "0.5s",
              }}
            >
              {techinalSkillsTitle}
            </AnimatedTitle>

            <Box
              sx={{
                opacity: 0,
                animation: `${fadeInUp} 0.5s ease-out forwards`,
                animationDelay: "0.6s",
              }}
            >
              <Skills skills={skills} />
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default AboutMe;